class FeaturedReviews extends HTMLElement {
  constructor() {
    super();

    this.splide = null;
    this.starRating = Number(this.dataset.starRating || 5);
    this.splideEl = this.querySelector(".js-featured-reviews-splide");
    this.slides = this.querySelectorAll(".featured-reviews__slide__wrapper");


    this.sessionKey = "tp-featured-reviews";
    this.loadedIndexes = new Set();
  }

  connectedCallback() {
    if (!this.splideEl || !window.Splide) return;

    this.initSplide();
    this.preloadInitialSlides();
    this.initRecommendationsToggle();
  }

  disconnectedCallback() {
    this.destroy();
  }

  initSplide() {
    if (this.splideEl.splideInstance) {
      this.splideEl.splideInstance.destroy();
    }

    this.splide = new Splide(this.splideEl, {
      perPage: 1,
      perMove: 1,
      arrows: true,
      pagination: false,
      drag: true,
      speed: 600,
      gap: 20,
      breakpoints: {
        989: {
          gap: 16,
          padding: { left: 16, right: 16 }
        }
      }
    });
    let dragStartX = 0;
    let dragStartIndex = 0;
    let isDragging = false;

    const track = this.splideEl.querySelector('.splide__track');

    track.addEventListener('touchstart', (e) => {
      if (window.innerWidth < 990) return;
      dragStartX = e.touches[0].clientX;
      dragStartIndex = this.splide.index;
      isDragging = true;
    }, { passive: true });

    track.addEventListener('touchmove', (e) => {
      if (!isDragging || window.innerWidth < 990) return;
      e.preventDefault();
    }, { passive: false });

    track.addEventListener('touchend', (e) => {
      if (!isDragging || window.innerWidth < 990) return;

      const dragEndX = e.changedTouches[0].clientX;
      const diff = dragStartX - dragEndX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0 && dragStartIndex < this.splide.length - 1) {
          this.splide.go(dragStartIndex + 1);
        } else if (diff < 0 && dragStartIndex > 0) {
          this.splide.go(dragStartIndex - 1);
        }
      }

      isDragging = false;
    });

    track.addEventListener('touchcancel', () => {
      isDragging = false;
    });

    track.addEventListener('mousedown', (e) => {
      if (window.innerWidth < 990) return;
      e.preventDefault();
      dragStartX = e.clientX;
      dragStartIndex = this.splide.index;
      isDragging = true;
    });

    track.addEventListener('mousemove', (e) => {
      if (!isDragging || window.innerWidth < 990) return;
      e.preventDefault();
    });

    track.addEventListener('mouseup', (e) => {
      if (!isDragging || window.innerWidth < 990) return;

      const dragEndX = e.clientX;
      const diff = dragStartX - dragEndX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0 && dragStartIndex < this.splide.length - 1) {
          this.splide.go(dragStartIndex + 1);
        } else if (diff < 0 && dragStartIndex > 0) {
          this.splide.go(dragStartIndex - 1);
        }
      }

      isDragging = false;
    });

    track.addEventListener('mouseleave', () => {
      isDragging = false;
    });

    const navigation = this.splide.root.querySelector('.carousel-navigation');
    const bar = navigation?.querySelector('.carousel-navigation-progress-bar');

    const updateProgress = () => {
      if (!bar) return;

      const total = this.splide.length;
      const perPage = this.splide.options.perPage;
      const currentIndex = this.splide.index;

      const covered = Math.min(currentIndex + perPage, total);
      bar.style.width = `${(covered / total) * 100}%`;
    };

    this.splide.on('overflow', isOverflow => {
      this.splide.options = { drag: isOverflow };
      if (navigation) {
        navigation.style.display = isOverflow ? 'flex' : 'none';
      }
    });

    this.splide.on("mounted move", index => {
      this.loadSlide(index);
      this.loadSlide(index + 1);
      updateProgress();
    });

    this.splide.on('active', (slide) => {
      const wrapper = slide.slide.querySelector('.featured-reviews__slide__wrapper');
      if (!wrapper) return;

      wrapper.classList.add('is-expanded');

      requestAnimationFrame(() => {
        setTimeout(() => {
          wrapper.classList.add('is-reveal');
        }, 350);
      });
    });

    this.splide.on('inactive', (slide) => {
      const wrapper = slide.slide.querySelector('.featured-reviews__slide__wrapper');
      if (!wrapper) return;

      wrapper.classList.remove('is-reveal');
      wrapper.classList.remove('is-expanded');
    });

    this.splide.on('move', () => {
      this.querySelectorAll('.is-recommendations-open')
        .forEach(el => el.classList.remove('is-recommendations-open'));
    });


    this.splideEl.splideInstance = this.splide;
    this.splide.mount();
  }

  destroy() {
    if (this.splideEl?.splideInstance) {
      this.splideEl.splideInstance.destroy();
      this.splideEl.splideInstance = null;
    }
  }

  buildStarsParam() {
    const stars = [];
    for (let i = this.starRating; i <= 5; i++) {
      stars.push(i);
    }
    return stars.join(",");
  }

  preloadInitialSlides() {
    this.loadSlide(0);
    this.loadSlide(1);
  }

  loadSlide(index) {
    if (this.loadedIndexes.has(index)) return;

    const slide = this.slides[index];
    if (!slide) return;

    this.loadedIndexes.add(index);

    const skus = slide.dataset.productSkus;
    if (!skus) return;

    this.fetchReview(skus, index);
  }

  fetchReview(skus, index) {
    const starsParam = this.buildStarsParam();
    const baseUrl = `https://api.trustpilot.com/v1/product-reviews/business-units/${window.tpk_business_unit_id}`;
    const normalUrl = `${baseUrl}/reviews?sku=${encodeURIComponent(
      skus
    )}&stars=${starsParam}&page=1&perPage=1`;
    const importedUrl = `${baseUrl}/imported-reviews?sku=${encodeURIComponent(
      skus
    )}&stars=${starsParam}&page=1&perPage=1`;
    const slide = this.slides[index];

    this.makeRequest(normalUrl)
      .then((normalData) => {
        if (normalData.productReviews && normalData.productReviews.length > 0) {
          const review = normalData.productReviews[0];
          this.renderReview(slide, review);
        } 
        else {
          return this.makeRequest(importedUrl).then((importedData) => {
            if (importedData.productReviews && importedData.productReviews.length > 0) {
              const review = importedData.productReviews[0];
              this.renderReview(slide, review);
            }
          });
        }
      })
      .catch((err) =>
        console.error("TrustPilot fetch error:", err)
      );
  }

  makeRequest = (url) =>
    fetch(url, {
      method: "GET",
      headers: {
        "apikey": window.tpk,
        "Content-Type": "application/json",
      },
    }).then((res) => {
      if (!res.ok) throw new Error("TrustPilot Network Error");
      return res.json();
    });

  isClamped(el) {
    return el.scrollHeight > el.clientHeight + 1;
  }

  renderReview(slide, review) {
    const contentEl = slide.querySelector(".js-review-content");
    const button = slide.querySelector(".js-read-more");
    const readMore = button.dataset.readMore;
    const readLess = button.dataset.readLess;
    
    slide.querySelector(".tp-avg-rating").textContent = review.stars;

    contentEl.textContent = `"${review.content || ""}"`;

    const consumerName =
      review.consumer?.displayName || review.consumer?.name || "";

    if (consumerName) {
      slide.querySelector(".featured-reviews__item__name").textContent =
        consumerName;
    }

    requestAnimationFrame(() => {
      if (window.innerWidth < 990 && this.isClamped(contentEl)) {
        button.hidden = false;
        button.textContent = readMore;

        button.onclick = () => {
          const expanded = contentEl.classList.toggle("is-expanded");
          button.textContent = expanded ? readLess : readMore;
        };
      } else {
        button.hidden = true;
      }
    });
  }

  initRecommendationsToggle() {
    this.querySelectorAll('.js-show-recommendations').forEach(button => {
      button.addEventListener('click', () => {
        if (window.innerWidth >= 990) return;

        const wrapper = button.closest('.featured-reviews__slide__wrapper');
        if (!wrapper) return;

        const isOpen = wrapper.classList.toggle('is-recommendations-open');

        if (isOpen) {
          requestAnimationFrame(() => {
            wrapper.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          });
        }
      });
    });
  }
}

customElements.define('featured-reviews', FeaturedReviews);